import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertCircle, RotateCcw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';

interface Props {
  children: ReactNode;
  /** Optional fallback override. */
  fallback?: (err: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Top-level error boundary. Catches render-time crashes anywhere downstream
 * and shows a friendly message with reload + back-to-home actions.
 *
 * Logging: in dev we console.error so devtools surface the trace; in prod
 * the user can reload, and if you wire Sentry/Datadog the listener fires.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error, info.componentStack);
  }

  reset = (): void => this.setState({ error: null });

  render(): ReactNode {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback(this.state.error, this.reset);
      return (
        <div className="min-h-screen flex items-center justify-center px-4 bg-nav-softer font-body text-nav-navy">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-md bg-nav-redTint flex items-center justify-center">
                  <AlertCircle className="h-4 w-4 text-nav-red" />
                </div>
                <CardTitle>Something broke on this page</CardTitle>
              </div>
              <CardDescription>
                A component crashed and we couldn't recover. Your session is safe and any
                auto-saved data is still in the database.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <pre className="text-xs bg-nav-hair border border-nav-card rounded p-3 overflow-x-auto text-nav-navy max-h-32">
                {this.state.error.message}
              </pre>
              <div className="flex gap-2 justify-end">
                <Button variant="ghost" onClick={() => (window.location.href = '/')}>
                  <Home className="h-4 w-4" />
                  Home
                </Button>
                <Button onClick={() => window.location.reload()}>
                  <RotateCcw className="h-4 w-4" />
                  Reload
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }
    return this.props.children;
  }
}
